import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useImmerReducer } from 'use-immer';
import { enableMapSet } from 'immer';
import StateContext from '../contexts/StateContext';
import DispatchContext from '../contexts/DispatchContext';
import studioReducer from '../reducers/studioReducer.js';
import { useLocalStorage } from '../hooks';
import { Provider } from 'jotai';

import { MediaCoreProvider } from '../components/MediaCoreProvider';
import Tooltip from '../common/Tooltip.jsx';
import ErrorBoundary from '../common/ErrorBoundary.jsx';
import HardwareTestApp from '../components/HardwareTestApp/HardwareTestApp';
import { getQueryParams } from '../helpers';

import Modal from 'react-modal';
import getStudioGlobalTrackingInitialState from '@app/store/studioGlobalTracking.js';
Modal.setAppElement('#root');

enableMapSet(); // Enables using a Set object in Immer

const prepareQueryParams = () => {
	let { mic, video } = getQueryParams();
	mic = !!mic;
	video = !!video;
	if (!mic && !video) {
		mic = true;
		video = true;
	}
	return { mic, video };
};

const { mic, video } = prepareQueryParams();

const initialState = {
	devices: [],
	headphones: '',
	activeAudioOutput: null,
	toasts: [],
	settingsDialogOpen: {
		isOpen: false,
		tabName: '',
	},
	target: {},
	streamGenerating: false,
	hasFilePermissions: false,
	constraints: {},
	error: '',
	pendingRequests: [],
	audioBlocked: false,
	studioSettings: {},
	recording: {},
	currentParticipant: {
		name: '',
		recording: false,
		volumeGain: 1,
		devices: [],
		activeDevices: { audio: null, video: null, output: null },
		hasVideo: video,
		hasAudio: mic,
	},
	studioGlobalTracking: getStudioGlobalTrackingInitialState({
		showData: { isAuthor: false, _id: 'id' },
		userData: { _id: 'id' },
		accountData: { _id: 'id' },
		role: 'guest',
	}),
};

const HardwareTester = (props) => {
	const [state, dispatch] = useImmerReducer(studioReducer, initialState);
	const [name, setName] = useLocalStorage('name', '');
	const { hiddenSelectors, testType, enabled } = useMemo(() => {
		let testType;
		const hiddenSelectors = { audioinput: true, videoinput: true, audiooutput: true };
		const enabled = { audio: false, video: false };
		if (video) {
			testType = 'webcam';
			enabled.video = true;
			delete hiddenSelectors.videoinput;
		}
		if (mic) {
			testType = 'mic';
			enabled.audio = true;
			delete hiddenSelectors.audioinput;
		}
		if (mic && video) {
			testType = 'webcam & mic';
		}
		return { testType, enabled, hiddenSelectors: Object.keys(hiddenSelectors) };
	}, []);

	return (
		<React.StrictMode>
			<Provider>
				<DispatchContext.Provider value={dispatch}>
					<StateContext.Provider value={state}>
						<MediaCoreProvider>
							<HardwareTestApp
								name={name}
								setName={setName}
								hiddenSelectors={hiddenSelectors}
								testType={testType}
								enabled={enabled}
							/>
							<Tooltip />
						</MediaCoreProvider>
					</StateContext.Provider>
				</DispatchContext.Provider>
			</Provider>
		</React.StrictMode>
	);
};

ReactDOM.render(
	<ErrorBoundary>
		<HardwareTester />
	</ErrorBoundary>,
	document.getElementById('root')
);
